// src/features/apps/BuildReadinessUtils.ts
//
// Pure rules of the Readiness card's "To build" group and the Build button's
// pre-click gate (build readiness, 3.34.5; owner ruling 2026-09-25: the button
// is never clickable while a prerequisite the factory would refuse is unmet,
// and the unmet ones are listed before the click, for every platform section).
// The factory is the authority on WHAT is unmet — its admission, exposed as a
// read (appfactory ADR-0040) — and this only decides how each token reads and
// where it is fixed. An unknown answer (loading, an older api or daemon, the
// network) is NOT a refusal: the group shows nothing new and the button keeps
// today's behaviour, with the factory's refusal after the click as the backstop.

import { FACTORY_BUILD_REFUSALS } from '@/lib/constants/appFactory';
import {
  BUILD_READINESS_COPY,
  BUILD_READINESS_IMAGES_UNCHECKED,
  BUILD_READINESS_ROWS,
  type BuildReadinessFixKind,
} from '@/lib/constants/buildReadiness';
import type { FactoryBuildReadiness } from '@/lib/types/AppFactory';

export type BuildReadinessTone = 'red' | 'neutral';

/** One row of the "To build" group. */
export interface BuildReadinessRow {
  /** The factory's token (or BUILD_READINESS_IMAGES_UNCHECKED for the neutral row). */
  reason: string;
  label: string;
  /** The customer sentence — the same words the refusal after a click would use. */
  sentence: string;
  /** Where it is fixed; null = nothing to click (a support row, the neutral row). */
  fix: BuildReadinessFixKind | null;
  tone: BuildReadinessTone;
}

export interface BuildReadinessView {
  /** The factory would admit a build now. */
  ready: boolean;
  rows: BuildReadinessRow[];
}

/** What the sentences name. */
export interface BuildReadinessContext {
  platformLabel: string;
  supportContact: string;
}

export class BuildReadinessUtils {
  /**
   * The group, or null = unknown (no answer yet, or a failed read): render
   * nothing new. Rows keep the factory's order (its capability gate first).
   * `imagesChecked: false` adds one neutral row unless the images are already
   * listed as missing.
   */
  static view(
    res: FactoryBuildReadiness | null | undefined,
    isError: boolean,
    ctx: BuildReadinessContext,
  ): BuildReadinessView | null {
    if (isError || !res) return null;
    const rows: BuildReadinessRow[] = (res.unmet ?? []).map((item) => {
      const info = BUILD_READINESS_ROWS[item.reason];
      return {
        reason: item.reason,
        label: BuildReadinessUtils.fill(info?.label ?? BUILD_READINESS_COPY.unknownLabel, ctx),
        sentence: BuildReadinessUtils.sentence(item.reason, item.message, ctx),
        // A support row's sentence names the contact; there is nothing to click.
        fix: info && info.fix !== 'support' ? info.fix : null,
        tone: 'red',
      };
    });
    if (res.imagesChecked === false && !rows.some((r) => r.reason === 'missing_images')) {
      rows.push({
        reason: BUILD_READINESS_IMAGES_UNCHECKED,
        label: BUILD_READINESS_ROWS.missing_images.label,
        sentence: BUILD_READINESS_COPY.imagesUnchecked,
        fix: null,
        tone: 'neutral',
      });
    }
    return { ready: res.buildable, rows };
  }

  /**
   * The Build button's `unmet` input: the red rows' sentences in order, empty
   * when everything holds, null when the answer is unknown (the button then
   * keeps today's behaviour).
   */
  static unmetSentences(view: BuildReadinessView | null): string[] | null {
    if (!view) return null;
    return view.rows.filter((r) => r.tone === 'red').map((r) => r.sentence);
  }

  /** A token's customer sentence (FACTORY_BUILD_REFUSALS), else the factory's own, placeholders filled. */
  static sentence(reason: string, message: string, ctx: BuildReadinessContext): string {
    return BuildReadinessUtils.fill(FACTORY_BUILD_REFUSALS[reason] ?? message, ctx);
  }

  static fill(s: string, ctx: BuildReadinessContext): string {
    return s.replace(/\{platform\}/g, ctx.platformLabel).replace(/\{supportContact\}/g, ctx.supportContact);
  }
}
